/**
 * useAudioControl — hook for computing audio pipeline gains.
 *
 * Centralizes the gain logic for mic (local), remote audio, and speaker output.
 * Used by VisioRoom to derive AudioPipelineConfig for useAntiLarsen.
 *
 * Gain curve: level 0 = gain 0.0, level 5 = gain 1.0 (unity), level 10 = gain 2.0
 */

import type { AudioPipelineConfig } from './useAntiLarsen'

export type MicMode = 'auto' | 'manual'
export type RemoteMode = 'auto' | 'manual'
export type SpeakerMode = 'antilarsen' | 'manual'

export interface AudioControlState {
  micMode: MicMode
  micLevel: number          // 0-10
  remoteMode: RemoteMode
  remoteLevel: number       // 0-10
  speakerMode: SpeakerMode
  speakerLevel: number      // 0-10
  speakerMuted: boolean
}

/** Convert a 0-10 level to a Web Audio gain value (0.0 - 2.0). */
export function levelToGain(level: number): number {
  return Math.max(0, Math.min(2, level / 5))
}

/** Derive AudioPipelineConfig from the current control state. */
export function buildPipelineConfig(state: AudioControlState): AudioPipelineConfig {
  return {
    enabled: true,
    antiLarsenActive: state.speakerMode === 'antilarsen',
    remoteLevel: state.remoteMode === 'manual' ? state.remoteLevel : 5,
    outputLevel: state.speakerMuted ? 0 : (state.speakerMode === 'manual' ? state.speakerLevel : 5),
  }
}
